import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { Menu, X, ChevronDown, User } from 'lucide-react'
import { NAV_ITEMS, COMPANY } from '@/lib/constants'
import Button from '@/components/common/Button'

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setOpenDropdown(null)
  }, [location])

  const isHomePage = location.pathname === '/'

  return (
    <header
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled || !isHomePage
          ? 'bg-white shadow-md'
          : 'bg-transparent'
      )}
      role="banner"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div
              className={clsx(
                'font-bold text-xl md:text-2xl transition-colors',
                isScrolled || !isHomePage ? 'text-primary-900' : 'text-white'
              )}
            >
              {COMPANY.name}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="relative">
                {item.children ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      className={clsx(
                        'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2',
                        isScrolled || !isHomePage
                          ? 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      )}
                      aria-expanded={openDropdown === item.label}
                      aria-haspopup="true"
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown className={clsx('h-4 w-4 transition-transform', openDropdown === item.label && 'rotate-180')} aria-hidden="true" />
                    </button>
                    {openDropdown === item.label && (
                      <div className="absolute top-full left-0 pt-2 w-56" role="menu" aria-label={`${item.label} submenu`}>
                        <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 focus:bg-primary-50 focus:text-primary-600 focus:outline-none"
                              role="menuitem"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={clsx(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      location.pathname === item.href
                        ? isScrolled || !isHomePage
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-white bg-white/20'
                        : isScrolled || !isHomePage
                        ? 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/login">
              <Button
                variant={isScrolled || !isHomePage ? 'ghost' : 'ghost'}
                size="sm"
                icon={<User className="h-4 w-4" />}
                className={clsx(
                  !isScrolled && isHomePage && 'text-white hover:bg-white/10'
                )}
              >
                Client Portal
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="secondary" size="sm">
                Contact Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <X
                className={clsx(
                  'h-6 w-6',
                  isScrolled || !isHomePage ? 'text-gray-700' : 'text-white'
                )}
              />
            ) : (
              <Menu
                className={clsx(
                  'h-6 w-6',
                  isScrolled || !isHomePage ? 'text-gray-700' : 'text-white'
                )}
              />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-4">
            {NAV_ITEMS.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-gray-700 font-medium"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === item.label ? null : item.label
                        )
                      }
                    >
                      {item.label}
                      <ChevronDown
                        className={clsx(
                          'h-4 w-4 transition-transform',
                          openDropdown === item.label && 'rotate-180'
                        )}
                      />
                    </button>
                    {openDropdown === item.label && (
                      <div className="bg-gray-50 py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block px-8 py-2 text-gray-600 hover:text-primary-600"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={clsx(
                      'block px-4 py-3 font-medium',
                      location.pathname === item.href
                        ? 'text-primary-600'
                        : 'text-gray-700'
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="px-4 pt-4 mt-4 border-t border-gray-100 space-y-3">
              <Link to="/login" className="block">
                <Button variant="outline" fullWidth icon={<User className="h-4 w-4" />}>
                  Client Portal
                </Button>
              </Link>
              <Link to="/contact" className="block">
                <Button variant="secondary" fullWidth>
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
